import React from "react";
import { Table, Typography } from "antd";
import { format } from "date-fns";

import StatusTag from "../../shared/StatusTag";

import {
	ClinicSettings,
	Invoice,
	InvoiceItem,
	LocalPaymentMethod,
} from "../../../types/finance/FinanceTypes";
import { TAX_CODE_LABELS, TaxCode } from "../../../utils/finance/gst";
import { formatCents, formatDollarString } from "../../../utils/finance/money";
import {
	invoiceDisplayNumber,
	invoiceDocumentTitle,
	invoiceStatusPresentation,
	showsGstSummary,
} from "../../../utils/finance/invoiceDisplay";

const { Text, Title } = Typography;

/**
 * The rendered invoice document.
 *
 * ── PRESENTATION-PURE, and that is a hard constraint ─────────────────────────────────────────
 *
 * No API calls, no React Query, no `Cookies.get`, no router, no context. Every value arrives as
 * a prop. That is what lets the **public** pay page (I9) render the identical document for a
 * patient who is not logged in and has no session at all — it fetches by token and hands
 * the result in. Adding one hook that reads a cookie here breaks that page rather than this one,
 * which is exactly the kind of failure that ships.
 *
 * ── The money rule ───────────────────────────────────────────────────────────────────────────
 *
 * A field the API sends as a dollar STRING is formatted AS a string (`formatDollarString`);
 * a field it only sends as integer cents is formatted from the integer (`formatCents`). Neither
 * path goes through `parseFloat`, so nothing on a legal document is rounded twice.
 *
 * ── The document type rule ───────────────────────────────────────────────────────────────────
 *
 * `documentType` is decided server-side at issue (ABN + GST registration) and frozen. When it is
 * `invoice` this component suppresses the GST summary and never prints the word "Tax".
 */

export interface InvoiceDocumentProps {
	invoice: Invoice;
	/**
	 * The clinic's CURRENT settings — used only for the parts that are not snapshotted onto the
	 * invoice (bank details, footer) and as a fallback for the identity block on a DRAFT, which
	 * has no snapshot yet.
	 *
	 * ⚠️ Never let these override a snapshotted value. An issued tax invoice must render
	 * identically forever; editing the clinic's address afterwards must not rewrite history.
	 */
	settings?: ClinicSettings | null;
	/** Rendered in the "Bill to" block. Resolved by the caller — this component looks nothing up. */
	patientName?: string;
	patientEmail?: string;
	/**
	 * Render `invoice.notes`. **Defaults to FALSE, and the default is the safety property.**
	 *
	 * ⚠️ `notes` is an INTERNAL field. `CreateInvoiceModal` labels it "Internal notes…" and staff
	 * type things there on that understanding — and this component is mounted on the PUBLIC,
	 * unauthenticated pay page (I9), where anyone holding the link can read whatever it renders.
	 * A staff-only surface opts IN; every other mount site gets the safe answer by doing nothing.
	 *
	 * If a patient-visible message is ever wanted on the document, it needs its own field with
	 * its own label — not this one re-purposed.
	 */
	showInternalNotes?: boolean;
}

const formatDate = (value?: string | null): string => {
	if (!value) {
		return "—";
	}
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? "—" : format(parsed, "dd MMM yyyy");
};

const InvoiceDocument: React.FC<InvoiceDocumentProps> = ({
	invoice,
	settings,
	patientName,
	patientEmail,
	showInternalNotes = false,
}) => {
	const title = invoiceDocumentTitle(invoice);
	const showGst = showsGstSummary(invoice);
	const status = invoiceStatusPresentation(invoice.effectiveStatus ?? invoice.status);

	// PAYLINK/PAYID: the payment block is NON-EXCLUSIVE — show EVERY selected+configured method
	// together. A selected method with nothing configured renders nothing (no fallback to another
	// method); the public page's EFT prompt covers the "nothing surfaces" case. This also covers a
	// Stripe invoice, whose methods default to [bank_transfer].
	//
	// ⚠️ The link scheme check mirrors `PublicInvoiceService.isHttpUrl` (the server already withholds
	// a non-http(s) link): this component renders on the unauthenticated public page, so a
	// `javascript:`/other scheme must never become clickable. A bad value ⇒ no link block.
	const methods = invoice.localPaymentMethods ?? [LocalPaymentMethod.BANK_TRANSFER];
	const paymentLink = settings?.invoicePaymentLink ?? null;
	const showPaymentLink =
		methods.includes(LocalPaymentMethod.PAYMENT_LINK) &&
		!!paymentLink &&
		/^https?:\/\//i.test(paymentLink);
	const hasBankDetails = !!(
		settings?.bankAccountName ||
		settings?.bankBsb ||
		settings?.bankAccountNumber
	);
	const showBankDetails = methods.includes(LocalPaymentMethod.BANK_TRANSFER) && hasBankDetails;
	const payIdType = settings?.payIdType ?? null;
	const payIdValue = settings?.payId ?? null;
	const showPayId = methods.includes(LocalPaymentMethod.PAYID) && !!payIdValue && !!payIdType;
	// Plain text, never a tel:/mailto: href — so it adds no href surface to the unauthenticated page.
	const payIdLabel = payIdType === "email" ? "Email" : "Mobile";

	// Snapshot first, settings only as a draft-time preview. See the prop docblock.
	const legalName = invoice.clinicLegalName ?? settings?.legalName ?? "";
	const tradingName = invoice.clinicLegalName ? null : settings?.tradingName ?? null;
	const abn = invoice.clinicAbn ?? settings?.abn ?? null;
	const address =
		invoice.clinicAddress ??
		[settings?.street, [settings?.suburb, settings?.state, settings?.postcode].filter(Boolean).join(" ")]
			.filter(Boolean)
			.join(", ");
	const contact =
		invoice.clinicContact ??
		[settings?.contactEmail, settings?.contactPhone].filter(Boolean).join(" | ");

	const columns = [
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
		},
		{
			title: "Qty",
			dataIndex: "quantity",
			key: "quantity",
			width: 70,
			align: "right" as const,
		},
		{
			title: "Unit price",
			key: "unitPrice",
			width: 110,
			align: "right" as const,
			// The dollar STRING the API sent, formatted as a string.
			render: (_: unknown, item: InvoiceItem) => formatDollarString(item.unitPrice),
		},
		...(showGst
			? [
					{
						title: "Tax",
						key: "taxCode",
						width: 140,
						render: (_: unknown, item: InvoiceItem) =>
							TAX_CODE_LABELS[item.taxCode as TaxCode] ?? item.taxCode,
					},
					{
						title: "GST",
						key: "lineTax",
						width: 100,
						align: "right" as const,
						render: (_: unknown, item: InvoiceItem) => formatCents(item.lineTaxCents),
					},
			  ]
			: []),
		{
			title: "Amount",
			key: "lineInc",
			width: 110,
			align: "right" as const,
			// No dollar-string equivalent exists for the inclusive line figure — `item.amount`
			// mirrors lineEx, not lineInc — so this one comes off the integer.
			render: (_: unknown, item: InvoiceItem) => (
				<Text strong>{formatCents(item.lineIncCents)}</Text>
			),
		},
	];

	const totalRow = (label: string, value: React.ReactNode, strong = false) => (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				gap: 24,
				padding: "2px 0",
			}}
		>
			<Text type={strong ? undefined : "secondary"} strong={strong}>
				{label}
			</Text>
			<Text strong={strong}>{value}</Text>
		</div>
	);

	return (
		<div data-testid="invoice-document" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{/* ── Clinic identity + document heading ── */}
			<div style={{ display: "flex", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
				<div>
					<Title level={4} style={{ margin: 0 }}>
						{legalName || "—"}
					</Title>
					{tradingName && (
						<div>
							<Text type="secondary">Trading as {tradingName}</Text>
						</div>
					)}
					{abn && (
						<div>
							<Text type="secondary">ABN {abn}</Text>
						</div>
					)}
					{address && (
						<div>
							<Text type="secondary">{address}</Text>
						</div>
					)}
					{contact && (
						<div>
							<Text type="secondary">{contact}</Text>
						</div>
					)}
				</div>

				<div style={{ textAlign: "right" }}>
					<Title level={4} style={{ margin: 0 }} data-testid="invoice-document-title">
						{title}
					</Title>
					<div>
						<Text strong data-testid="invoice-document-number">
							{invoiceDisplayNumber(invoice)}
						</Text>
					</div>
					<div style={{ marginTop: 4 }}>
						{/*
						  `StatusTag` is a pure antd wrapper (no API, no cookies), so it does not
						  break this component's presentation-purity contract. The colour and label
						  are passed explicitly — its own map has no `sent`/`overdue` row.
						*/}
						<StatusTag
							status={invoice.effectiveStatus ?? invoice.status}
							color={status.color}
							label={status.label}
						/>
					</div>
				</div>
			</div>

			{/* ── Parties + dates ── */}
			<div style={{ display: "flex", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
				<div>
					<Text type="secondary" style={{ fontSize: 12 }}>
						BILL TO
					</Text>
					<div>
						<Text strong>{patientName ?? `Patient ${invoice.userId}`}</Text>
					</div>
					{patientEmail && (
						<div>
							<Text type="secondary">{patientEmail}</Text>
						</div>
					)}
				</div>
				<div style={{ textAlign: "right" }}>
					<div>
						<Text type="secondary">Issue date </Text>
						<Text>{formatDate(invoice.issueDate)}</Text>
					</div>
					<div>
						<Text type="secondary">Due date </Text>
						<Text>{formatDate(invoice.dueDate)}</Text>
					</div>
					{invoice.paidDate && (
						<div>
							<Text type="secondary">Paid </Text>
							<Text>{formatDate(invoice.paidDate)}</Text>
						</div>
					)}
				</div>
			</div>

			{/* ── Lines ── */}
			<Table
				columns={columns}
				dataSource={invoice.items ?? []}
				rowKey={(item: InvoiceItem) => item.id ?? `${item.description}-${item.sortOrder ?? 0}`}
				pagination={false}
				size="small"
				data-testid="invoice-document-lines"
			/>

			{/* ── Totals ── */}
			<div style={{ display: "flex", justifyContent: "flex-end" }}>
				<div style={{ minWidth: 280 }} data-testid="invoice-document-totals">
					{/*
					  The GST summary is suppressed outright for a plain `invoice`: the clinic is
					  not entitled to charge GST, so both the ex-GST subtotal and the GST line
					  would be meaningless (and the word "Tax" must not appear at all).
					*/}
					{showGst && totalRow("Subtotal (ex. GST)", formatDollarString(invoice.subtotal))}
					{showGst && totalRow("GST", formatDollarString(invoice.tax))}
					{totalRow("Total", formatDollarString(invoice.total), true)}
					{Number(invoice.amountPaidCents ?? 0) !== 0 &&
						totalRow("Paid", formatCents(invoice.amountPaidCents))}
					{totalRow("Balance due", formatCents(invoice.balanceDueCents), true)}
				</div>
			</div>

			{/* ── Payment instructions + footer (current settings, never snapshotted) ── */}
			{/*
			  NON-EXCLUSIVE: every selected+configured method renders together under one heading —
			  the payment link, the bank details and the PayID, in that order. Nothing renders for a
			  selected-but-unconfigured method.
			*/}
			{(showPaymentLink || showBankDetails || showPayId) && (
				<div
					data-testid="invoice-document-payment-details"
					style={{ display: "flex", flexDirection: "column", gap: 8 }}
				>
					<Text type="secondary" style={{ fontSize: 12 }}>
						PAYMENT OPTIONS
					</Text>
					{showPaymentLink && (
						<div data-testid="invoice-document-payment-link">
							<Text>Pay online at </Text>
							<Text strong>{paymentLink}</Text>
						</div>
					)}
					{showBankDetails && (
						<div data-testid="invoice-document-bank-details">
							{settings?.bankAccountName && (
								<div>
									<Text>{settings.bankAccountName}</Text>
								</div>
							)}
							{settings?.bankBsb && (
								<div>
									<Text type="secondary">BSB {settings.bankBsb}</Text>
								</div>
							)}
							{settings?.bankAccountNumber && (
								<div>
									<Text type="secondary">Account {settings.bankAccountNumber}</Text>
								</div>
							)}
						</div>
					)}
					{showPayId && (
						<div data-testid="invoice-document-payid">
							{/* Plain text — PayID (Mobile|Email): <value>. Never a tel:/mailto: href. */}
							<Text strong>PayID ({payIdLabel}): </Text>
							<Text>{payIdValue}</Text>
						</div>
					)}
				</div>
			)}

			{settings?.invoiceFooterText && (
				<Text type="secondary" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
					{settings.invoiceFooterText}
				</Text>
			)}

			{/* Staff-only. See `showInternalNotes` — this block does not render for a patient. */}
			{showInternalNotes && invoice.notes && (
				<div data-testid="invoice-document-internal-notes">
					<Text type="secondary" style={{ fontSize: 12 }}>
						INTERNAL NOTES — not shown to the patient
					</Text>
					<div>
						<Text style={{ whiteSpace: "pre-wrap" }}>{invoice.notes}</Text>
					</div>
				</div>
			)}
		</div>
	);
};

export default InvoiceDocument;
